/**
 * CISA Zero Trust Evidence Model
 *
 * Defines the evidence-backed scoring framework for the CISA Maturity Model.
 * This enables three-layer scoring: Capability Coverage, Configuration Completeness, Evidence Confidence
 *
 * Reference: CISA Zero Trust Maturity Model v2.0
 */

/**
 * Evidence types with metadata for tracking and expiration
 */
export const EVIDENCE_TYPES = {
  POLICY_EXPORT: {
    key: 'POLICY_EXPORT',
    label: 'Policy Export',
    description: 'Exported policy configuration from IdP, firewall, or security tool',
    icon: 'FileText',
    expiryDays: 90,
    color: '#6366f1'
  },
  CONFIG_SNAPSHOT: {
    key: 'CONFIG_SNAPSHOT',
    label: 'Config Snapshot',
    description: 'Point-in-time configuration settings from a system',
    icon: 'Settings',
    expiryDays: 30,
    color: '#8b5cf6'
  },
  LOG_COVERAGE: {
    key: 'LOG_COVERAGE',
    label: 'Log Coverage',
    description: 'Evidence of logging and monitoring coverage',
    icon: 'Activity',
    expiryDays: 7,
    color: '#06b6d4'
  },
  GOVERNANCE_ARTIFACT: {
    key: 'GOVERNANCE_ARTIFACT',
    label: 'Governance Doc',
    description: 'Policy documents, procedures, or governance records',
    icon: 'Shield',
    expiryDays: 365,
    color: '#10b981'
  },
  MANUAL_ATTESTATION: {
    key: 'MANUAL_ATTESTATION',
    label: 'Manual Attestation',
    description: 'Human attestation with notes and justification',
    icon: 'UserCheck',
    expiryDays: 180,
    color: '#f59e0b'
  },
  AUTOMATED_SCAN: {
    key: 'AUTOMATED_SCAN',
    label: 'Automated Scan',
    description: 'Results from automated compliance or security scans',
    icon: 'Radar',
    expiryDays: 7,
    color: '#ef4444'
  },
  INTEGRATION_API: {
    key: 'INTEGRATION_API',
    label: 'API Integration',
    description: 'Live data from integrated security tools via API',
    icon: 'Plug',
    expiryDays: 1,
    color: '#ec4899'
  }
};

/**
 * Evidence status options
 */
export const EVIDENCE_STATUS = {
  VERIFIED: { key: 'VERIFIED', label: 'Verified', color: '#10b981', icon: 'CheckCircle' },
  PARTIAL: { key: 'PARTIAL', label: 'Partial', color: '#f59e0b', icon: 'AlertCircle' },
  MISSING: { key: 'MISSING', label: 'Missing', color: '#ef4444', icon: 'XCircle' },
  EXPIRED: { key: 'EXPIRED', label: 'Expired', color: '#6b7280', icon: 'Clock' },
  NOT_APPLICABLE: { key: 'NOT_APPLICABLE', label: 'N/A', color: '#9ca3af', icon: 'Minus' }
};

/**
 * Three-layer scoring weights
 * These determine how each layer contributes to the overall derived maturity level
 */
export const SCORING_WEIGHTS = {
  capabilityCoverage: 0.40,        // Do we have products for this function?
  configurationCompleteness: 0.35, // Are the products properly configured?
  evidenceConfidence: 0.25         // Is our assessment verified with evidence?
};

/**
 * Thresholds for deriving maturity levels from weighted scores
 */
export const LEVEL_THRESHOLDS = {
  OPTIMAL: 85,     // >= 85% weighted score
  ADVANCED: 65,    // >= 65% weighted score
  INITIAL: 40,     // >= 40% weighted score
  TRADITIONAL: 0   // < 40% weighted score
};

/**
 * Evidence check types
 */
export const CHECK_TYPES = {
  REQUIRED: { key: 'REQUIRED', label: 'Required', weight: 1.0 },
  RECOMMENDED: { key: 'RECOMMENDED', label: 'Recommended', weight: 0.5 },
  OPTIONAL: { key: 'OPTIONAL', label: 'Optional', weight: 0.25 }
};

/**
 * Evidence checks per function per maturity level
 * Each check represents a verification point that confirms the organization has achieved that level
 */
export const EVIDENCE_CHECKS = {
  // ============ IDENTITY PILLAR ============
  ID_AUTH: {
    INITIAL: [
      { id: 'ID_AUTH_INIT_01', question: 'Is MFA deployed for all user accounts?', type: 'REQUIRED', source: 'IdP policy export', productHint: 'IdP (Okta, Azure AD, Ping)' },
      { id: 'ID_AUTH_INIT_02', question: 'Are multiple authentication factors validated before access?', type: 'REQUIRED', source: 'Authentication logs', productHint: null },
      { id: 'ID_AUTH_INIT_03', question: 'Is there a documented MFA rollout and exceptions policy?', type: 'RECOMMENDED', source: 'Governance documentation', productHint: null }
    ],
    ADVANCED: [
      { id: 'ID_AUTH_ADV_01', question: 'Is phishing-resistant MFA (FIDO2/PIV) deployed?', type: 'REQUIRED', source: 'FIDO2 policy config export', productHint: 'YubiKey, Windows Hello, PIV' },
      { id: 'ID_AUTH_ADV_02', question: 'Are legacy authentication methods (SMS, voice) disabled?', type: 'REQUIRED', source: 'IdP authentication settings', productHint: null },
      { id: 'ID_AUTH_ADV_03', question: 'Is passwordless authentication available to users?', type: 'RECOMMENDED', source: 'Auth method inventory', productHint: 'Okta FastPass, Windows Hello' },
      { id: 'ID_AUTH_ADV_04', question: 'Are context-aware authentication policies enforced?', type: 'REQUIRED', source: 'Conditional access policy export', productHint: null }
    ],
    OPTIMAL: [
      { id: 'ID_AUTH_OPT_01', question: 'Is authentication continuously evaluated throughout sessions?', type: 'REQUIRED', source: 'Session policy + reauthentication logs', productHint: 'Continuous Auth / UEBA' },
      { id: 'ID_AUTH_OPT_02', question: 'Do risk signals dynamically adjust authentication requirements?', type: 'REQUIRED', source: 'Risk-based conditional access rules', productHint: null },
      { id: 'ID_AUTH_OPT_03', question: 'Is step-up authentication triggered automatically on risk?', type: 'REQUIRED', source: 'Step-up auth event logs', productHint: null }
    ]
  },
  ID_STORES: {
    INITIAL: [
      { id: 'ID_STORES_INIT_01', question: 'Are identity stores consolidated where possible?', type: 'REQUIRED', source: 'Identity store inventory', productHint: 'Entra ID, Okta UD' },
      { id: 'ID_STORES_INIT_02', question: 'Is automated provisioning enabled for new users?', type: 'REQUIRED', source: 'SCIM/provisioning config', productHint: null },
      { id: 'ID_STORES_INIT_03', question: 'Is there basic lifecycle management (joiner/mover/leaver)?', type: 'RECOMMENDED', source: 'HR integration documentation', productHint: null }
    ],
    ADVANCED: [
      { id: 'ID_STORES_ADV_01', question: 'Is there an authoritative identity source established?', type: 'REQUIRED', source: 'Identity architecture diagram', productHint: null },
      { id: 'ID_STORES_ADV_02', question: 'Is identity lifecycle fully automated?', type: 'REQUIRED', source: 'Automated workflow config', productHint: 'SailPoint, Saviynt' },
      { id: 'ID_STORES_ADV_03', question: 'Is identity federation enabled across systems?', type: 'REQUIRED', source: 'Federation trust configuration', productHint: null }
    ],
    OPTIMAL: [
      { id: 'ID_STORES_OPT_01', question: 'Is there unified identity fabric with real-time sync?', type: 'REQUIRED', source: 'Real-time sync configuration', productHint: null },
      { id: 'ID_STORES_OPT_02', question: 'Is self-service identity management enabled with controls?', type: 'RECOMMENDED', source: 'Self-service portal config', productHint: null },
      { id: 'ID_STORES_OPT_03', question: 'Are identities available across all enterprise systems?', type: 'REQUIRED', source: 'Cross-system identity mapping', productHint: null }
    ]
  },
  ID_RISK: {
    INITIAL: [
      { id: 'ID_RISK_INIT_01', question: 'Is basic risk scoring deployed for privileged accounts?', type: 'REQUIRED', source: 'PAM risk scoring config', productHint: 'CyberArk, BeyondTrust' },
      { id: 'ID_RISK_INIT_02', question: 'Are manual risk assessments conducted during access reviews?', type: 'REQUIRED', source: 'Access review records', productHint: null },
      { id: 'ID_RISK_INIT_03', question: 'Is there a documented identity risk assessment process?', type: 'RECOMMENDED', source: 'Risk assessment SOP', productHint: null }
    ],
    ADVANCED: [
      { id: 'ID_RISK_ADV_01', question: 'Is continuous identity risk scoring integrated with access?', type: 'REQUIRED', source: 'UEBA + access control integration', productHint: 'Exabeam, Securonix' },
      { id: 'ID_RISK_ADV_02', question: 'Are behavioral analytics informing identity risk levels?', type: 'REQUIRED', source: 'UEBA behavioral baseline config', productHint: null },
      { id: 'ID_RISK_ADV_03', question: 'Are high-risk identity events triggering automated response?', type: 'RECOMMENDED', source: 'SOAR playbook for identity risk', productHint: null }
    ],
    OPTIMAL: [
      { id: 'ID_RISK_OPT_01', question: 'Is ML/AI-driven identity risk assessment deployed?', type: 'REQUIRED', source: 'ML model configuration + accuracy metrics', productHint: null },
      { id: 'ID_RISK_OPT_02', question: 'Do risk scores dynamically adjust permissions across resources?', type: 'REQUIRED', source: 'Dynamic permission policy rules', productHint: null },
      { id: 'ID_RISK_OPT_03', question: 'Is predictive identity threat detection enabled?', type: 'RECOMMENDED', source: 'Predictive model outputs', productHint: null }
    ]
  },
  ID_ACCESS: {
    INITIAL: [
      { id: 'ID_ACCESS_INIT_01', question: 'Is RBAC implemented across applications?', type: 'REQUIRED', source: 'RBAC role definitions export', productHint: 'Entra ID, Okta, SailPoint' },
      { id: 'ID_ACCESS_INIT_02', question: 'Is periodic access certification conducted?', type: 'REQUIRED', source: 'Access certification campaign records', productHint: null },
      { id: 'ID_ACCESS_INIT_03', question: 'Is JIT access available for privileged accounts?', type: 'RECOMMENDED', source: 'PAM JIT configuration', productHint: 'CyberArk, HashiCorp Vault' }
    ],
    ADVANCED: [
      { id: 'ID_ACCESS_ADV_01', question: 'Are ABAC policies combining multiple attributes?', type: 'REQUIRED', source: 'ABAC policy definitions', productHint: null },
      { id: 'ID_ACCESS_ADV_02', question: 'Is JIT access standard for most privileged operations?', type: 'REQUIRED', source: 'JIT access usage logs', productHint: null },
      { id: 'ID_ACCESS_ADV_03', question: 'Are access reviews automated and regular?', type: 'REQUIRED', source: 'Automated review schedule config', productHint: null }
    ],
    OPTIMAL: [
      { id: 'ID_ACCESS_OPT_01', question: 'Are dynamic access policies continuously evaluated?', type: 'REQUIRED', source: 'Continuous policy evaluation logs', productHint: null },
      { id: 'ID_ACCESS_OPT_02', question: 'Is zero standing privilege achieved?', type: 'REQUIRED', source: 'Standing privilege audit report', productHint: null },
      { id: 'ID_ACCESS_OPT_03', question: 'Are access decisions automated based on real-time context?', type: 'REQUIRED', source: 'Context-based access decision logs', productHint: null }
    ]
  },

  // ============ DEVICES PILLAR ============
  DEV_POLICY: {
    INITIAL: [
      { id: 'DEV_POLICY_INIT_01', question: 'Are device compliance policies defined?', type: 'REQUIRED', source: 'MDM compliance policy export', productHint: 'Intune, Jamf, Workspace ONE' },
      { id: 'DEV_POLICY_INIT_02', question: 'Is regular compliance checking enabled?', type: 'REQUIRED', source: 'Compliance check schedule config', productHint: null },
      { id: 'DEV_POLICY_INIT_03', question: 'Are non-compliant devices flagged or restricted?', type: 'RECOMMENDED', source: 'Non-compliance action logs', productHint: null }
    ],
    ADVANCED: [
      { id: 'DEV_POLICY_ADV_01', question: 'Is real-time compliance monitoring deployed?', type: 'REQUIRED', source: 'Real-time compliance dashboard', productHint: 'Tanium, Qualys VMDR' },
      { id: 'DEV_POLICY_ADV_02', question: 'Is automated remediation for non-compliant devices enabled?', type: 'REQUIRED', source: 'Auto-remediation workflow config', productHint: null },
      { id: 'DEV_POLICY_ADV_03', question: 'Is compliance status integrated with access decisions?', type: 'REQUIRED', source: 'Conditional access + compliance integration', productHint: null }
    ],
    OPTIMAL: [
      { id: 'DEV_POLICY_OPT_01', question: 'Is continuous device posture assessment deployed?', type: 'REQUIRED', source: 'Continuous posture check logs', productHint: null },
      { id: 'DEV_POLICY_OPT_02', question: 'Is dynamic policy enforcement based on risk signals?', type: 'REQUIRED', source: 'Dynamic policy rules config', productHint: null },
      { id: 'DEV_POLICY_OPT_03', question: 'Are compliance failures triggering real-time access revocation?', type: 'REQUIRED', source: 'Access revocation event logs', productHint: null }
    ]
  },
  DEV_ASSET: {
    INITIAL: [
      { id: 'DEV_ASSET_INIT_01', question: 'Is there a centralized asset inventory?', type: 'REQUIRED', source: 'Asset inventory export', productHint: 'ServiceNow, Axonius, Armis' },
      { id: 'DEV_ASSET_INIT_02', question: 'Is there basic supply chain awareness?', type: 'RECOMMENDED', source: 'Vendor/supplier documentation', productHint: null },
      { id: 'DEV_ASSET_INIT_03', question: 'Are inventory updates scheduled regularly?', type: 'REQUIRED', source: 'Inventory update schedule', productHint: null }
    ],
    ADVANCED: [
      { id: 'DEV_ASSET_ADV_01', question: 'Is automated asset discovery deployed?', type: 'REQUIRED', source: 'Asset discovery scan config', productHint: 'Tanium, Qualys' },
      { id: 'DEV_ASSET_ADV_02', question: 'Is supply chain risk tracked per asset?', type: 'REQUIRED', source: 'SBOM + supply chain risk data', productHint: null },
      { id: 'DEV_ASSET_ADV_03', question: 'Is near real-time asset visibility achieved?', type: 'REQUIRED', source: 'Asset update frequency metrics', productHint: null }
    ],
    OPTIMAL: [
      { id: 'DEV_ASSET_OPT_01', question: 'Is complete real-time asset visibility achieved?', type: 'REQUIRED', source: 'Real-time asset dashboard', productHint: null },
      { id: 'DEV_ASSET_OPT_02', question: 'Is full supply chain risk integrated?', type: 'REQUIRED', source: 'Supply chain risk integration config', productHint: null },
      { id: 'DEV_ASSET_OPT_03', question: 'Is asset lifecycle fully automated?', type: 'REQUIRED', source: 'Lifecycle automation workflow', productHint: null }
    ]
  },
  DEV_ACCESS: {
    INITIAL: [
      { id: 'DEV_ACCESS_INIT_01', question: 'Is device identity verified before access?', type: 'REQUIRED', source: 'Device identity verification config', productHint: 'ZPA, Prisma Access' },
      { id: 'DEV_ACCESS_INIT_02', question: 'Are device certificates or tokens used?', type: 'REQUIRED', source: 'Device certificate enrollment logs', productHint: null },
      { id: 'DEV_ACCESS_INIT_03', question: 'Is VPN dependency reduced for managed devices?', type: 'RECOMMENDED', source: 'VPN-less access adoption metrics', productHint: null }
    ],
    ADVANCED: [
      { id: 'DEV_ACCESS_ADV_01', question: 'Is device health integrated into access decisions?', type: 'REQUIRED', source: 'Device health + access policy integration', productHint: 'Cloudflare Access, Duo' },
      { id: 'DEV_ACCESS_ADV_02', question: 'Is granular resource access based on device trust?', type: 'REQUIRED', source: 'Device trust tier definitions', productHint: null },
      { id: 'DEV_ACCESS_ADV_03', question: 'Are BYOD devices assessed before access?', type: 'RECOMMENDED', source: 'BYOD posture check config', productHint: null }
    ],
    OPTIMAL: [
      { id: 'DEV_ACCESS_OPT_01', question: 'Is continuous device trust evaluation enabled?', type: 'REQUIRED', source: 'Continuous trust check logs', productHint: null },
      { id: 'DEV_ACCESS_OPT_02', question: 'Do dynamic access adjustments occur in real-time?', type: 'REQUIRED', source: 'Dynamic access adjustment logs', productHint: null },
      { id: 'DEV_ACCESS_OPT_03', question: 'Is device posture tied to every resource request?', type: 'REQUIRED', source: 'Per-request posture verification logs', productHint: null }
    ]
  },
  DEV_THREAT: {
    INITIAL: [
      { id: 'DEV_THREAT_INIT_01', question: 'Is EDR deployed on all managed devices?', type: 'REQUIRED', source: 'EDR deployment coverage report', productHint: 'CrowdStrike, Defender, SentinelOne' },
      { id: 'DEV_THREAT_INIT_02', question: 'Is automated threat detection enabled?', type: 'REQUIRED', source: 'EDR detection policy config', productHint: null },
      { id: 'DEV_THREAT_INIT_03', question: 'Is there some automated response capability?', type: 'RECOMMENDED', source: 'Automated response action logs', productHint: null }
    ],
    ADVANCED: [
      { id: 'DEV_THREAT_ADV_01', question: 'Is XDR integration across endpoints deployed?', type: 'REQUIRED', source: 'XDR integration config', productHint: 'Cortex XDR, Carbon Black' },
      { id: 'DEV_THREAT_ADV_02', question: 'Is behavioral threat detection enabled?', type: 'REQUIRED', source: 'Behavioral detection rule config', productHint: null },
      { id: 'DEV_THREAT_ADV_03', question: 'Is automated containment enabled?', type: 'REQUIRED', source: 'Auto-containment policy config', productHint: null }
    ],
    OPTIMAL: [
      { id: 'DEV_THREAT_OPT_01', question: 'Is AI-driven threat protection deployed?', type: 'REQUIRED', source: 'AI/ML threat model config', productHint: null },
      { id: 'DEV_THREAT_OPT_02', question: 'Are predictive threat capabilities enabled?', type: 'REQUIRED', source: 'Predictive threat detection logs', productHint: null },
      { id: 'DEV_THREAT_OPT_03', question: 'Is threat detection integrated with ZT access decisions?', type: 'REQUIRED', source: 'Threat + access integration config', productHint: null }
    ]
  },

  // ============ NETWORKS PILLAR ============
  NET_SEG: {
    INITIAL: [
      { id: 'NET_SEG_INIT_01', question: 'Is basic network segmentation by zone or function deployed?', type: 'REQUIRED', source: 'VLAN/zone inventory', productHint: 'Cisco ISE, Aruba ClearPass' },
      { id: 'NET_SEG_INIT_02', question: 'Are VLANs and basic firewalls controlling internal traffic?', type: 'REQUIRED', source: 'Firewall rule export', productHint: null },
      { id: 'NET_SEG_INIT_03', question: 'Is there documentation of segmentation architecture?', type: 'RECOMMENDED', source: 'Network architecture diagram', productHint: null }
    ],
    ADVANCED: [
      { id: 'NET_SEG_ADV_01', question: 'Is micro-segmentation deployed at workload level?', type: 'REQUIRED', source: 'Microseg policy export', productHint: 'Illumio, Guardicore, NSX' },
      { id: 'NET_SEG_ADV_02', question: 'Are identity-aware network policies enforced?', type: 'REQUIRED', source: 'SGT/identity policy config', productHint: 'Cisco TrustSec' },
      { id: 'NET_SEG_ADV_03', question: 'Is software-defined perimeter (SDP) deployed?', type: 'RECOMMENDED', source: 'SDP configuration', productHint: null }
    ],
    OPTIMAL: [
      { id: 'NET_SEG_OPT_01', question: 'Is dynamic micro-segmentation with real-time policy updates deployed?', type: 'REQUIRED', source: 'Dynamic policy update logs', productHint: null },
      { id: 'NET_SEG_OPT_02', question: 'Is full application-aware segmentation achieved?', type: 'REQUIRED', source: 'Application-level policy config', productHint: null },
      { id: 'NET_SEG_OPT_03', question: 'Is zero implicit trust achieved for all network traffic?', type: 'REQUIRED', source: 'Default-deny policy verification', productHint: null }
    ]
  },
  NET_TRAFFIC: {
    INITIAL: [
      { id: 'NET_TRAFFIC_INIT_01', question: 'Is network traffic logging and monitoring deployed?', type: 'REQUIRED', source: 'NetFlow/IPFIX config', productHint: 'Stealthwatch, ExtraHop' },
      { id: 'NET_TRAFFIC_INIT_02', question: 'Is some internal traffic inspection enabled?', type: 'REQUIRED', source: 'Internal traffic monitoring scope', productHint: null },
      { id: 'NET_TRAFFIC_INIT_03', question: 'Is SIEM integration for network events enabled?', type: 'REQUIRED', source: 'SIEM network source config', productHint: null }
    ],
    ADVANCED: [
      { id: 'NET_TRAFFIC_ADV_01', question: 'Is full traffic visibility including encrypted traffic analysis deployed?', type: 'REQUIRED', source: 'ETA/SSL inspection config', productHint: 'Cisco ETA, Vectra' },
      { id: 'NET_TRAFFIC_ADV_02', question: 'Is Network Detection and Response (NDR) deployed?', type: 'REQUIRED', source: 'NDR deployment verification', productHint: 'Darktrace, Corelight' },
      { id: 'NET_TRAFFIC_ADV_03', question: 'Are behavioral baselines established for network traffic?', type: 'REQUIRED', source: 'Traffic baseline configuration', productHint: null }
    ],
    OPTIMAL: [
      { id: 'NET_TRAFFIC_OPT_01', question: 'Is AI-driven traffic analysis deployed?', type: 'REQUIRED', source: 'AI/ML traffic analysis config', productHint: null },
      { id: 'NET_TRAFFIC_OPT_02', question: 'Is real-time anomaly detection enabled?', type: 'REQUIRED', source: 'Anomaly detection alert config', productHint: null },
      { id: 'NET_TRAFFIC_OPT_03', question: 'Is automated response to traffic-based threats enabled?', type: 'REQUIRED', source: 'Auto-response playbook config', productHint: null }
    ]
  },
  NET_ENCRYPT: {
    INITIAL: [
      { id: 'NET_ENCRYPT_INIT_01', question: 'Are encryption policies for sensitive internal traffic defined?', type: 'REQUIRED', source: 'Encryption policy documentation', productHint: 'HashiCorp Vault PKI' },
      { id: 'NET_ENCRYPT_INIT_02', question: 'Is TLS enforced for most external communications?', type: 'REQUIRED', source: 'TLS configuration audit', productHint: null },
      { id: 'NET_ENCRYPT_INIT_03', question: 'Is there a timeline for deprecating cleartext protocols?', type: 'RECOMMENDED', source: 'Protocol deprecation roadmap', productHint: null }
    ],
    ADVANCED: [
      { id: 'NET_ENCRYPT_ADV_01', question: 'Is encryption required for all internal traffic?', type: 'REQUIRED', source: 'Internal encryption policy verification', productHint: 'Istio, Linkerd, MACsec' },
      { id: 'NET_ENCRYPT_ADV_02', question: 'Is mTLS deployed for service-to-service communication?', type: 'REQUIRED', source: 'mTLS configuration export', productHint: 'Service mesh config' },
      { id: 'NET_ENCRYPT_ADV_03', question: 'Is certificate lifecycle management automated?', type: 'REQUIRED', source: 'Cert rotation configuration', productHint: 'Venafi, DigiCert' }
    ],
    OPTIMAL: [
      { id: 'NET_ENCRYPT_OPT_01', question: 'Is universal encryption with automated key management deployed?', type: 'REQUIRED', source: 'Universal encryption verification', productHint: null },
      { id: 'NET_ENCRYPT_OPT_02', question: 'Is post-quantum readiness being addressed?', type: 'RECOMMENDED', source: 'PQ migration roadmap', productHint: null },
      { id: 'NET_ENCRYPT_OPT_03', question: 'Is full visibility with encrypted traffic analysis achieved?', type: 'REQUIRED', source: 'ETA coverage verification', productHint: null }
    ]
  },
  NET_RESIL: {
    INITIAL: [
      { id: 'NET_RESIL_INIT_01', question: 'Are resilience requirements defined?', type: 'REQUIRED', source: 'Resilience requirements documentation', productHint: 'Cloudflare, AWS Shield' },
      { id: 'NET_RESIL_INIT_02', question: 'Is some automated failover deployed?', type: 'REQUIRED', source: 'Failover configuration', productHint: null },
      { id: 'NET_RESIL_INIT_03', question: 'Is basic DDoS mitigation enabled?', type: 'REQUIRED', source: 'DDoS protection config', productHint: null }
    ],
    ADVANCED: [
      { id: 'NET_RESIL_ADV_01', question: 'Is multi-region/zone deployment achieved?', type: 'REQUIRED', source: 'Multi-region architecture documentation', productHint: 'SD-WAN, Global LB' },
      { id: 'NET_RESIL_ADV_02', question: 'Is automated failover and recovery deployed?', type: 'REQUIRED', source: 'Automated failover test results', productHint: null },
      { id: 'NET_RESIL_ADV_03', question: 'Is advanced DDoS protection (L7) deployed?', type: 'REQUIRED', source: 'L7 DDoS protection config', productHint: 'Akamai Prolexic, Magic Transit' }
    ],
    OPTIMAL: [
      { id: 'NET_RESIL_OPT_01', question: 'Is self-healing network architecture deployed?', type: 'REQUIRED', source: 'Self-healing automation config', productHint: null },
      { id: 'NET_RESIL_OPT_02', question: 'Is predictive resilience enabled?', type: 'REQUIRED', source: 'Predictive monitoring config', productHint: 'ThousandEyes' },
      { id: 'NET_RESIL_OPT_03', question: 'Is continuous resilience testing (chaos engineering) performed?', type: 'REQUIRED', source: 'Chaos engineering test records', productHint: 'Gremlin, Chaos Mesh' }
    ]
  },

  // ============ APPLICATIONS PILLAR ============
  APP_ACCESS: {
    INITIAL: [
      { id: 'APP_ACCESS_INIT_01', question: 'Is application-level authentication deployed?', type: 'REQUIRED', source: 'Application auth config', productHint: 'Okta, Ping, Entra ID' },
      { id: 'APP_ACCESS_INIT_02', question: 'Are basic authorization controls implemented?', type: 'REQUIRED', source: 'Authorization policy export', productHint: null },
      { id: 'APP_ACCESS_INIT_03', question: 'Is SSO implementation started?', type: 'REQUIRED', source: 'SSO app inventory', productHint: null }
    ],
    ADVANCED: [
      { id: 'APP_ACCESS_ADV_01', question: 'Is context-aware application access deployed?', type: 'REQUIRED', source: 'Context-aware access policies', productHint: 'ZPA, Cloudflare Access' },
      { id: 'APP_ACCESS_ADV_02', question: 'Is fine-grained authorization implemented?', type: 'REQUIRED', source: 'Fine-grained authz policy export', productHint: null },
      { id: 'APP_ACCESS_ADV_03', question: 'Is SSO with MFA fully deployed?', type: 'REQUIRED', source: 'SSO + MFA coverage report', productHint: null }
    ],
    OPTIMAL: [
      { id: 'APP_ACCESS_OPT_01', question: 'Is continuous authorization per transaction deployed?', type: 'REQUIRED', source: 'Per-transaction authz logs', productHint: null },
      { id: 'APP_ACCESS_OPT_02', question: 'Is real-time risk-based access enabled?', type: 'REQUIRED', source: 'Risk-based access config', productHint: null },
      { id: 'APP_ACCESS_OPT_03', question: 'Is seamless UX achieved with strong security?', type: 'RECOMMENDED', source: 'User experience metrics', productHint: null }
    ]
  },
  APP_THREAT: {
    INITIAL: [
      { id: 'APP_THREAT_INIT_01', question: 'Is WAF deployed with custom rules?', type: 'REQUIRED', source: 'WAF rule configuration', productHint: 'Cloudflare WAF, AWS WAF' },
      { id: 'APP_THREAT_INIT_02', question: 'Is basic runtime protection enabled?', type: 'REQUIRED', source: 'Runtime protection config', productHint: null },
      { id: 'APP_THREAT_INIT_03', question: 'Is automated blocking of known threats enabled?', type: 'REQUIRED', source: 'Automated blocking logs', productHint: null }
    ],
    ADVANCED: [
      { id: 'APP_THREAT_ADV_01', question: 'Is RASP integration deployed?', type: 'REQUIRED', source: 'RASP deployment verification', productHint: 'Contrast Security, Signal Sciences' },
      { id: 'APP_THREAT_ADV_02', question: 'Are API security gateways deployed?', type: 'REQUIRED', source: 'API gateway configuration', productHint: null },
      { id: 'APP_THREAT_ADV_03', question: 'Is behavioral threat detection for apps enabled?', type: 'REQUIRED', source: 'App behavioral detection config', productHint: null }
    ],
    OPTIMAL: [
      { id: 'APP_THREAT_OPT_01', question: 'Is AI-driven application threat protection deployed?', type: 'REQUIRED', source: 'AI threat protection config', productHint: null },
      { id: 'APP_THREAT_OPT_02', question: 'Is predictive threat prevention enabled?', type: 'REQUIRED', source: 'Predictive prevention logs', productHint: null },
      { id: 'APP_THREAT_OPT_03', question: 'Is full SecOps integration achieved?', type: 'REQUIRED', source: 'SecOps integration verification', productHint: null }
    ]
  },
  APP_ACCESSIBLE: {
    INITIAL: [
      { id: 'APP_ACCESSIBLE_INIT_01', question: 'Are some applications accessible without VPN?', type: 'REQUIRED', source: 'VPN-less app inventory', productHint: 'ZPA, Cloudflare Access' },
      { id: 'APP_ACCESSIBLE_INIT_02', question: 'Is basic ZTNA deployed for select applications?', type: 'REQUIRED', source: 'ZTNA application list', productHint: null },
      { id: 'APP_ACCESSIBLE_INIT_03', question: 'Is there a roadmap for VPN elimination?', type: 'RECOMMENDED', source: 'VPN migration roadmap', productHint: null }
    ],
    ADVANCED: [
      { id: 'APP_ACCESSIBLE_ADV_01', question: 'Are most applications accessible via ZTNA?', type: 'REQUIRED', source: 'ZTNA coverage report', productHint: 'Prisma Access, Netskope' },
      { id: 'APP_ACCESSIBLE_ADV_02', question: 'Is access model consistent regardless of location?', type: 'REQUIRED', source: 'Location-independent access verification', productHint: null },
      { id: 'APP_ACCESSIBLE_ADV_03', question: 'Is VPN usage significantly reduced?', type: 'REQUIRED', source: 'VPN usage metrics', productHint: null }
    ],
    OPTIMAL: [
      { id: 'APP_ACCESSIBLE_OPT_01', question: 'Are all applications securely accessible from anywhere?', type: 'REQUIRED', source: 'Universal ZTNA verification', productHint: null },
      { id: 'APP_ACCESSIBLE_OPT_02', question: 'Is VPN fully eliminated for app access?', type: 'REQUIRED', source: 'VPN elimination verification', productHint: null },
      { id: 'APP_ACCESSIBLE_OPT_03', question: 'Is network location irrelevant for access?', type: 'REQUIRED', source: 'Location-agnostic access verification', productHint: null }
    ]
  },
  APP_SECDEV: {
    INITIAL: [
      { id: 'APP_SECDEV_INIT_01', question: 'Is security integrated into SDLC?', type: 'REQUIRED', source: 'SDLC security documentation', productHint: 'Snyk, Checkmarx, Veracode' },
      { id: 'APP_SECDEV_INIT_02', question: 'Is automated security testing in CI/CD enabled?', type: 'REQUIRED', source: 'CI/CD security scan config', productHint: null },
      { id: 'APP_SECDEV_INIT_03', question: 'Is security training provided for developers?', type: 'REQUIRED', source: 'Developer security training records', productHint: null }
    ],
    ADVANCED: [
      { id: 'APP_SECDEV_ADV_01', question: 'Is shift-left security fully implemented?', type: 'REQUIRED', source: 'Shift-left implementation verification', productHint: 'GitGuardian, Semgrep' },
      { id: 'APP_SECDEV_ADV_02', question: 'Are automated security gates in pipeline enforced?', type: 'REQUIRED', source: 'Pipeline security gate config', productHint: null },
      { id: 'APP_SECDEV_ADV_03', question: 'Is infrastructure as code security implemented?', type: 'REQUIRED', source: 'IaC security scan config', productHint: 'Bridgecrew, Checkov' }
    ],
    OPTIMAL: [
      { id: 'APP_SECDEV_OPT_01', question: 'Is security by design achieved?', type: 'REQUIRED', source: 'Security by design documentation', productHint: null },
      { id: 'APP_SECDEV_OPT_02', question: 'Is secure pipeline fully automated?', type: 'REQUIRED', source: 'Automated pipeline verification', productHint: null },
      { id: 'APP_SECDEV_OPT_03', question: 'Is continuous compliance verification enabled?', type: 'REQUIRED', source: 'Continuous compliance config', productHint: null }
    ]
  },
  APP_TESTING: {
    INITIAL: [
      { id: 'APP_TESTING_INIT_01', question: 'Is regular SAST/DAST scanning deployed?', type: 'REQUIRED', source: 'SAST/DAST scan schedule', productHint: 'Qualys WAS, OWASP ZAP' },
      { id: 'APP_TESTING_INIT_02', question: 'Is vulnerability management program established?', type: 'REQUIRED', source: 'Vuln management process doc', productHint: null },
      { id: 'APP_TESTING_INIT_03', question: 'Is prioritized remediation implemented?', type: 'REQUIRED', source: 'Remediation SLA documentation', productHint: null }
    ],
    ADVANCED: [
      { id: 'APP_TESTING_ADV_01', question: 'Is continuous automated testing deployed?', type: 'REQUIRED', source: 'Continuous testing config', productHint: 'HackerOne, Bugcrowd' },
      { id: 'APP_TESTING_ADV_02', question: 'Is SCA for dependencies deployed?', type: 'REQUIRED', source: 'SCA tool configuration', productHint: 'Snyk, Dependabot' },
      { id: 'APP_TESTING_ADV_03', question: 'Is bug bounty program active?', type: 'RECOMMENDED', source: 'Bug bounty program details', productHint: 'HackerOne, Synack' }
    ],
    OPTIMAL: [
      { id: 'APP_TESTING_OPT_01', question: 'Is real-time security testing in production deployed?', type: 'REQUIRED', source: 'Production security testing config', productHint: null },
      { id: 'APP_TESTING_OPT_02', question: 'Is automated exploitation testing enabled?', type: 'REQUIRED', source: 'Automated exploit testing logs', productHint: null },
      { id: 'APP_TESTING_OPT_03', question: 'Is full coverage with intelligent prioritization achieved?', type: 'REQUIRED', source: 'Coverage + prioritization metrics', productHint: null }
    ]
  },

  // ============ DATA PILLAR ============
  DATA_INV: {
    INITIAL: [
      { id: 'DATA_INV_INIT_01', question: 'Is basic data inventory initiated?', type: 'REQUIRED', source: 'Data inventory export', productHint: 'BigID, OneTrust, Varonis' },
      { id: 'DATA_INV_INIT_02', question: 'Are known repositories cataloged?', type: 'REQUIRED', source: 'Repository catalog', productHint: null },
      { id: 'DATA_INV_INIT_03', question: 'Is some automated discovery enabled?', type: 'RECOMMENDED', source: 'Discovery scan config', productHint: null }
    ],
    ADVANCED: [
      { id: 'DATA_INV_ADV_01', question: 'Is comprehensive data inventory achieved?', type: 'REQUIRED', source: 'Comprehensive inventory verification', productHint: 'Alation, Collibra' },
      { id: 'DATA_INV_ADV_02', question: 'Is automated data discovery deployed?', type: 'REQUIRED', source: 'Automated discovery config', productHint: null },
      { id: 'DATA_INV_ADV_03', question: 'Is data lineage tracking implemented?', type: 'REQUIRED', source: 'Data lineage configuration', productHint: null }
    ],
    OPTIMAL: [
      { id: 'DATA_INV_OPT_01', question: 'Is real-time data inventory with full lineage deployed?', type: 'REQUIRED', source: 'Real-time inventory verification', productHint: null },
      { id: 'DATA_INV_OPT_02', question: 'Are automated updates as data moves enabled?', type: 'REQUIRED', source: 'Automated update verification', productHint: null },
      { id: 'DATA_INV_OPT_03', question: 'Is complete visibility across all environments achieved?', type: 'REQUIRED', source: 'Cross-environment visibility verification', productHint: null }
    ]
  },
  DATA_CAT: {
    INITIAL: [
      { id: 'DATA_CAT_INIT_01', question: 'Is classification policy defined?', type: 'REQUIRED', source: 'Classification policy document', productHint: 'Microsoft Purview, AWS Macie' },
      { id: 'DATA_CAT_INIT_02', question: 'Is some automated classification enabled?', type: 'REQUIRED', source: 'Auto-classification config', productHint: null },
      { id: 'DATA_CAT_INIT_03', question: 'Are labels applied to new data?', type: 'REQUIRED', source: 'Labeling policy configuration', productHint: null }
    ],
    ADVANCED: [
      { id: 'DATA_CAT_ADV_01', question: 'Is automated classification for most data deployed?', type: 'REQUIRED', source: 'Classification coverage report', productHint: 'BigID, Varonis' },
      { id: 'DATA_CAT_ADV_02', question: 'Is consistent labeling across repositories achieved?', type: 'REQUIRED', source: 'Cross-repo labeling verification', productHint: null },
      { id: 'DATA_CAT_ADV_03', question: 'Does classification drive access controls?', type: 'REQUIRED', source: 'Classification-access integration', productHint: null }
    ],
    OPTIMAL: [
      { id: 'DATA_CAT_OPT_01', question: 'Is AI-driven continuous classification deployed?', type: 'REQUIRED', source: 'AI classification configuration', productHint: null },
      { id: 'DATA_CAT_OPT_02', question: 'Is real-time reclassification on context change enabled?', type: 'REQUIRED', source: 'Dynamic reclassification verification', productHint: null },
      { id: 'DATA_CAT_OPT_03', question: 'Is full integration with access controls achieved?', type: 'REQUIRED', source: 'Access control integration verification', productHint: null }
    ]
  },
  DATA_AVAIL: {
    INITIAL: [
      { id: 'DATA_AVAIL_INIT_01', question: 'Are RPO/RTO targets defined?', type: 'REQUIRED', source: 'RPO/RTO documentation', productHint: 'Veeam, Cohesity, Rubrik' },
      { id: 'DATA_AVAIL_INIT_02', question: 'Are regular backups with testing performed?', type: 'REQUIRED', source: 'Backup test records', productHint: null },
      { id: 'DATA_AVAIL_INIT_03', question: 'Is some automation of recovery processes started?', type: 'RECOMMENDED', source: 'Recovery automation config', productHint: null }
    ],
    ADVANCED: [
      { id: 'DATA_AVAIL_ADV_01', question: 'Is automated backup and recovery deployed?', type: 'REQUIRED', source: 'Automated backup config', productHint: null },
      { id: 'DATA_AVAIL_ADV_02', question: 'Is multi-region data availability achieved?', type: 'REQUIRED', source: 'Multi-region replication config', productHint: null },
      { id: 'DATA_AVAIL_ADV_03', question: 'Is regular DR testing conducted?', type: 'REQUIRED', source: 'DR test records', productHint: null }
    ],
    OPTIMAL: [
      { id: 'DATA_AVAIL_OPT_01', question: 'Is zero-downtime data availability achieved?', type: 'REQUIRED', source: 'Uptime SLA verification', productHint: null },
      { id: 'DATA_AVAIL_OPT_02', question: 'Is automated failover and recovery complete?', type: 'REQUIRED', source: 'Automated failover verification', productHint: null },
      { id: 'DATA_AVAIL_OPT_03', question: 'Is continuous DR validation enabled?', type: 'REQUIRED', source: 'Continuous DR validation config', productHint: null }
    ]
  },
  DATA_ACCESS: {
    INITIAL: [
      { id: 'DATA_ACCESS_INIT_01', question: 'Are data-level access controls implemented?', type: 'REQUIRED', source: 'Data access control config', productHint: 'Varonis, Netwrix' },
      { id: 'DATA_ACCESS_INIT_02', question: 'Are basic DLP policies deployed?', type: 'REQUIRED', source: 'DLP policy configuration', productHint: null },
      { id: 'DATA_ACCESS_INIT_03', question: 'Are regular data access reviews conducted?', type: 'REQUIRED', source: 'Data access review records', productHint: null }
    ],
    ADVANCED: [
      { id: 'DATA_ACCESS_ADV_01', question: 'Are fine-grained data access policies deployed?', type: 'REQUIRED', source: 'Fine-grained access policy export', productHint: 'Immuta, Privacera' },
      { id: 'DATA_ACCESS_ADV_02', question: 'Are context-aware access decisions enabled?', type: 'REQUIRED', source: 'Context-aware access config', productHint: null },
      { id: 'DATA_ACCESS_ADV_03', question: 'Is automated access governance deployed?', type: 'REQUIRED', source: 'Automated governance verification', productHint: null }
    ],
    OPTIMAL: [
      { id: 'DATA_ACCESS_OPT_01', question: 'Is dynamic data access based on real-time context deployed?', type: 'REQUIRED', source: 'Dynamic access verification', productHint: null },
      { id: 'DATA_ACCESS_OPT_02', question: 'Is zero trust data access achieved?', type: 'REQUIRED', source: 'ZT data access verification', productHint: null },
      { id: 'DATA_ACCESS_OPT_03', question: 'Is full audit trail with analytics deployed?', type: 'REQUIRED', source: 'Audit trail + analytics config', productHint: null }
    ]
  },
  DATA_ENCRYPT: {
    INITIAL: [
      { id: 'DATA_ENCRYPT_INIT_01', question: 'Is encryption policy defined?', type: 'REQUIRED', source: 'Encryption policy document', productHint: 'HashiCorp Vault, AWS KMS' },
      { id: 'DATA_ENCRYPT_INIT_02', question: 'Is key management established?', type: 'REQUIRED', source: 'Key management configuration', productHint: null },
      { id: 'DATA_ENCRYPT_INIT_03', question: 'Is sensitive data encrypted at rest?', type: 'REQUIRED', source: 'At-rest encryption verification', productHint: null }
    ],
    ADVANCED: [
      { id: 'DATA_ENCRYPT_ADV_01', question: 'Is full encryption at rest achieved?', type: 'REQUIRED', source: 'Full at-rest encryption audit', productHint: 'Thales CipherTrust, Fortanix' },
      { id: 'DATA_ENCRYPT_ADV_02', question: 'Is automated key rotation deployed?', type: 'REQUIRED', source: 'Key rotation configuration', productHint: null },
      { id: 'DATA_ENCRYPT_ADV_03', question: 'Is all data in transit encrypted?', type: 'REQUIRED', source: 'In-transit encryption verification', productHint: null }
    ],
    OPTIMAL: [
      { id: 'DATA_ENCRYPT_OPT_01', question: 'Is universal encryption with centralized key management deployed?', type: 'REQUIRED', source: 'Universal encryption verification', productHint: null },
      { id: 'DATA_ENCRYPT_OPT_02', question: 'Is post-quantum cryptography readiness addressed?', type: 'RECOMMENDED', source: 'PQ readiness assessment', productHint: null },
      { id: 'DATA_ENCRYPT_OPT_03', question: 'Is encryption integrated with access controls?', type: 'REQUIRED', source: 'Encryption-access integration config', productHint: null }
    ]
  },

  // ============ CROSS-CUTTING CAPABILITIES ============
  VISIBILITY: {
    INITIAL: [
      { id: 'VIS_INIT_01', question: 'Is centralized logging (SIEM) deployed?', type: 'REQUIRED', source: 'SIEM deployment verification', productHint: 'Splunk, Microsoft Sentinel' },
      { id: 'VIS_INIT_02', question: 'Are basic correlation rules implemented?', type: 'REQUIRED', source: 'SIEM correlation rule export', productHint: null },
      { id: 'VIS_INIT_03', question: 'Is regular log review process established?', type: 'REQUIRED', source: 'Log review SOP', productHint: null }
    ],
    ADVANCED: [
      { id: 'VIS_ADV_01', question: 'Is full telemetry integration achieved?', type: 'REQUIRED', source: 'Telemetry source inventory', productHint: 'Chronicle, Sumo Logic' },
      { id: 'VIS_ADV_02', question: 'Are advanced analytics and correlation deployed?', type: 'REQUIRED', source: 'Advanced analytics configuration', productHint: null },
      { id: 'VIS_ADV_03', question: 'Is automated alerting enabled?', type: 'REQUIRED', source: 'Alert configuration export', productHint: null }
    ],
    OPTIMAL: [
      { id: 'VIS_OPT_01', question: 'Is AI-driven analytics across all pillars deployed?', type: 'REQUIRED', source: 'AI analytics configuration', productHint: null },
      { id: 'VIS_OPT_02', question: 'Is predictive threat detection enabled?', type: 'REQUIRED', source: 'Predictive detection verification', productHint: null },
      { id: 'VIS_OPT_03', question: 'Is real-time visibility into all ZT components achieved?', type: 'REQUIRED', source: 'ZT visibility dashboard', productHint: null }
    ]
  },
  AUTOMATION: {
    INITIAL: [
      { id: 'AUTO_INIT_01', question: 'Is SOAR platform deployed?', type: 'REQUIRED', source: 'SOAR deployment verification', productHint: 'XSOAR, Splunk SOAR, Swimlane' },
      { id: 'AUTO_INIT_02', question: 'Are playbooks for common scenarios created?', type: 'REQUIRED', source: 'Playbook inventory', productHint: null },
      { id: 'AUTO_INIT_03', question: 'Is some automated response enabled?', type: 'REQUIRED', source: 'Automated response logs', productHint: null }
    ],
    ADVANCED: [
      { id: 'AUTO_ADV_01', question: 'Is extensive security workflow automation deployed?', type: 'REQUIRED', source: 'Workflow automation inventory', productHint: 'Tines, Torq' },
      { id: 'AUTO_ADV_02', question: 'Is orchestration across multiple tools enabled?', type: 'REQUIRED', source: 'Cross-tool orchestration config', productHint: null },
      { id: 'AUTO_ADV_03', question: 'Is automated remediation deployed?', type: 'REQUIRED', source: 'Automated remediation logs', productHint: null }
    ],
    OPTIMAL: [
      { id: 'AUTO_OPT_01', question: 'Is full automation of security operations achieved?', type: 'REQUIRED', source: 'Full automation verification', productHint: null },
      { id: 'AUTO_OPT_02', question: 'Are self-healing systems deployed?', type: 'REQUIRED', source: 'Self-healing system config', productHint: null },
      { id: 'AUTO_OPT_03', question: 'Is AI-driven orchestration across all pillars enabled?', type: 'REQUIRED', source: 'AI orchestration verification', productHint: null }
    ]
  },
  GOVERNANCE: {
    INITIAL: [
      { id: 'GOV_INIT_01', question: 'Are ZT policies documented?', type: 'REQUIRED', source: 'ZT policy documentation', productHint: 'ServiceNow GRC, RSA Archer' },
      { id: 'GOV_INIT_02', question: 'Are regular compliance assessments conducted?', type: 'REQUIRED', source: 'Compliance assessment records', productHint: null },
      { id: 'GOV_INIT_03', question: 'Is governance structure defined?', type: 'REQUIRED', source: 'Governance org chart / RACI', productHint: null }
    ],
    ADVANCED: [
      { id: 'GOV_ADV_01', question: 'Is automated policy enforcement deployed?', type: 'REQUIRED', source: 'Policy enforcement automation config', productHint: 'Drata, Vanta' },
      { id: 'GOV_ADV_02', question: 'Is continuous compliance monitoring enabled?', type: 'REQUIRED', source: 'Continuous compliance dashboard', productHint: null },
      { id: 'GOV_ADV_03', question: 'Is metrics-driven governance implemented?', type: 'REQUIRED', source: 'Governance metrics dashboard', productHint: null }
    ],
    OPTIMAL: [
      { id: 'GOV_OPT_01', question: 'Is policy as code deployed?', type: 'REQUIRED', source: 'Policy as code repository', productHint: null },
      { id: 'GOV_OPT_02', question: 'Is real-time compliance achieved?', type: 'REQUIRED', source: 'Real-time compliance verification', productHint: null },
      { id: 'GOV_OPT_03', question: 'Is adaptive governance based on risk and context enabled?', type: 'REQUIRED', source: 'Adaptive governance config', productHint: null }
    ]
  }
};

/**
 * Helper: Get all evidence checks for a function
 */
export function getEvidenceChecksForFunction(functionCode) {
  return EVIDENCE_CHECKS[functionCode] || {};
}

/**
 * Helper: Get evidence checks for a specific function/level
 */
export function getEvidenceChecksForLevel(functionCode, level) {
  return EVIDENCE_CHECKS[functionCode]?.[level] || [];
}

/**
 * Helper: Count total checks for a function across all levels
 */
export function countTotalChecks(functionCode) {
  const checks = EVIDENCE_CHECKS[functionCode];
  if (!checks) return 0;
  return Object.values(checks).flat().length;
}

/**
 * Helper: Get all check IDs for a function
 */
export function getAllCheckIds(functionCode) {
  const checks = EVIDENCE_CHECKS[functionCode];
  if (!checks) return [];
  return Object.values(checks).flat().map(c => c.id);
}

/**
 * Helper: Get the function code from a check ID
 * Check IDs follow the pattern: FUNCTION_CODE_LEVEL_NUMBER
 */
export function getFunctionFromCheckId(checkId) {
  // Extract function code from check ID (e.g., ID_AUTH_INIT_01 -> ID_AUTH)
  const parts = checkId.split('_');
  if (parts.length < 3) return null;

  // Handle special cases like ID_AUTH, NET_SEG, etc.
  // The function code is everything up to the level indicator (INIT/ADV/OPT)
  const levelIndicators = ['INIT', 'ADV', 'OPT'];
  let functionParts = [];
  for (const part of parts) {
    if (levelIndicators.includes(part)) break;
    functionParts.push(part);
  }
  return functionParts.join('_');
}

/**
 * Calculate capability coverage score (0-100)
 * Based on: products adopted vs products recommended for the current level
 */
export function calculateCapabilityCoverage(adoptedProducts, functionCode, currentLevel) {
  if (currentLevel === 'TRADITIONAL') return 0; // No products needed at TRADITIONAL

  // Count adopted products that are "IN_USE" or "PLANNED"
  const activeProducts = (adoptedProducts || []).filter(
    p => p.status === 'IN_USE' || p.status === 'PLANNED'
  ).length;

  // At least 1 active product = some coverage
  // 2+ products = good coverage
  // This is a simplified model; real implementation might compare against expected products
  if (activeProducts === 0) return 0;
  if (activeProducts === 1) return 50;
  if (activeProducts === 2) return 75;
  return 100;
}

/**
 * Calculate configuration completeness score (0-100)
 * Based on: config steps completed vs total config steps for the level
 */
export function calculateConfigurationCompleteness(configCompletions, functionCode, currentLevel) {
  if (currentLevel === 'TRADITIONAL') return 100; // No configs needed at TRADITIONAL

  const levelConfigs = configCompletions?.[currentLevel];
  if (!levelConfigs || levelConfigs.total === 0) return 0;

  return Math.round((levelConfigs.completed / levelConfigs.total) * 100);
}

/**
 * Calculate evidence confidence score (0-100)
 * Based on: evidence checks verified vs total required checks
 */
export function calculateEvidenceConfidence(evidenceChecks, functionCode, currentLevel) {
  if (currentLevel === 'TRADITIONAL') return 100; // No evidence needed at TRADITIONAL

  const levelChecks = getEvidenceChecksForLevel(functionCode, currentLevel);
  if (levelChecks.length === 0) return 100;

  const requiredChecks = levelChecks.filter(c => c.type === 'REQUIRED');
  const verifiedRequired = requiredChecks.filter(c => {
    const status = evidenceChecks?.[c.id]?.status;
    return status === 'VERIFIED';
  }).length;

  // Weight: Required checks are 100% of score for confidence
  if (requiredChecks.length === 0) return 100;
  return Math.round((verifiedRequired / requiredChecks.length) * 100);
}

/**
 * Calculate weighted score and derive maturity level
 */
export function calculateDerivedScore(
  capabilityCoverage,
  configurationCompleteness,
  evidenceConfidence
) {
  const weighted =
    capabilityCoverage * SCORING_WEIGHTS.capabilityCoverage +
    configurationCompleteness * SCORING_WEIGHTS.configurationCompleteness +
    evidenceConfidence * SCORING_WEIGHTS.evidenceConfidence;

  let derivedLevel = 'TRADITIONAL';
  if (weighted >= LEVEL_THRESHOLDS.OPTIMAL) {
    derivedLevel = 'OPTIMAL';
  } else if (weighted >= LEVEL_THRESHOLDS.ADVANCED) {
    derivedLevel = 'ADVANCED';
  } else if (weighted >= LEVEL_THRESHOLDS.INITIAL) {
    derivedLevel = 'INITIAL';
  }

  return {
    weightedScore: Math.round(weighted),
    derivedLevel
  };
}

/**
 * Check if evidence is expired
 */
export function isEvidenceExpired(evidence) {
  if (!evidence?.expiresAt) return false;
  return new Date(evidence.expiresAt) < new Date();
}

/**
 * Calculate expiration date based on evidence type
 */
export function calculateExpirationDate(evidenceType) {
  const typeConfig = EVIDENCE_TYPES[evidenceType];
  if (!typeConfig) return null;

  const expirationDate = new Date();
  expirationDate.setDate(expirationDate.getDate() + typeConfig.expiryDays);
  return expirationDate.toISOString();
}

/**
 * Get expiration warning level
 * Returns: 'expired' | 'expiring_soon' | 'valid' | null
 */
export function getExpirationWarning(expiresAt) {
  if (!expiresAt) return null;

  const expDate = new Date(expiresAt);
  const now = new Date();
  const daysUntilExpiry = Math.ceil((expDate - now) / (1000 * 60 * 60 * 24));

  if (daysUntilExpiry < 0) return 'expired';
  if (daysUntilExpiry <= 7) return 'expiring_soon';
  return 'valid';
}

/**
 * Default evidence object structure
 */
export function createDefaultEvidence(functionCode, evidenceType, source) {
  return {
    id: `${functionCode}_${evidenceType}_${Date.now()}`,
    functionCode,
    type: evidenceType,
    source: source || '',
    status: 'MISSING',
    verifiedAt: null,
    verifiedBy: null,
    expiresAt: null,
    productId: null,
    attachmentUrl: null,
    notes: ''
  };
}

/**
 * Default evidence check status object
 */
export function createDefaultCheckStatus(checkId) {
  return {
    checkId,
    status: 'MISSING',
    verifiedAt: null,
    verifiedBy: null,
    notes: '',
    evidenceIds: []
  };
}
